/**
 * Presentation of the application forms of the career site, saved on the firm (`jobBoardForm`) by the
 * customization page: an introduction above the form, the thank-you screen shown once the application is
 * sent, and whether the parts of the form are numbered. Texts are plain text in French and English.
 */
import { emptyCareerText } from './careerSections.ts'
import type { TCareerText } from './careerSections.ts'

export const CAREER_FORM_LIMITS = { title: 160, text: 2000 } as const

export type TCareerForm = {
  intro: TCareerText
  successTitle: TCareerText
  successMessage: TCareerText
  /** The parts of the form (you, your application, documents...) show a number */
  numbered: boolean
}

export const defaultCareerForm = (): TCareerForm => ({
  intro: emptyCareerText(),
  successTitle: emptyCareerText(),
  successMessage: emptyCareerText(),
  numbered: true,
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const text = (value: unknown, max: number): TCareerText => {
  const input = isRecord(value) ? value : {}
  const read = (language: 'FR' | 'EN') => (typeof input[language] === 'string' ? (input[language] as string).slice(0, max) : '')
  return { FR: read('FR'), EN: read('EN') }
}

export const sanitizeCareerForm = (value: unknown): TCareerForm => {
  const input = isRecord(value) ? value : {}
  return {
    intro: text(input.intro, CAREER_FORM_LIMITS.text),
    successTitle: text(input.successTitle, CAREER_FORM_LIMITS.title),
    successMessage: text(input.successMessage, CAREER_FORM_LIMITS.text),
    numbered: typeof input.numbered === 'boolean' ? input.numbered : true,
  }
}
